import { useMemo } from "react";
import { toggleSort as sharedToggleSort, type SortConfig } from "@/shared/utils";
import type { Volunteer } from "../types";

export type VolunteerSortKey = "full_name" | "region" | "projects_count" | "is_active";

export type { SortConfig };

export function useVolunteersSort(
  volunteers: Volunteer[],
  sortConfig: SortConfig<VolunteerSortKey> | null,
  locale: string,
): Volunteer[] {
  return useMemo(() => {
    if (!sortConfig) return volunteers;

    const { key, direction } = sortConfig;
    const regionKey = `name_${locale}` as keyof Volunteer["region"];

    return [...volunteers].sort((a, b) => {
      let cmp = 0;

      if (key === "full_name") {
        cmp = a.full_name.localeCompare(b.full_name);
      } else if (key === "region") {
        const aName = a.region ? (a.region[regionKey] as string) || a.region.name_ru || "" : "";
        const bName = b.region ? (b.region[regionKey] as string) || b.region.name_ru || "" : "";
        if (!aName && bName) return 1;
        if (aName && !bName) return -1;
        cmp = aName.localeCompare(bName);
      } else if (key === "projects_count") {
        cmp = a.projects_count - b.projects_count;
      } else if (key === "is_active") {
        cmp = Number(b.is_active) - Number(a.is_active);
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [volunteers, sortConfig, locale]);
}

export function toggleSort(currentSort: SortConfig<VolunteerSortKey> | null, newKey: VolunteerSortKey): SortConfig<VolunteerSortKey> {
  return sharedToggleSort(currentSort, newKey);
}
